/**
 * sitePlot.ts — pure, side-effect-free transforms for the site-map render.
 *
 * All functions take metres-from-top-left coordinates and the plot-plan
 * dimensions, and return either:
 *   • percentage offsets for 2D DOM/SVG overlay, or
 *   • centre-origin scene coords for a Three.js ground (origin = centre of ground).
 *
 * These are the only unit-testable bits of the site-map feature; everything
 * else (React, Three.js, auth'd fetches) is integration-only.
 */

export interface PlotPct {
  /** Left offset as a percentage of the plot-plan image width (0–100). */
  leftPct: number;
  /** Top offset as a percentage of the plot-plan image height (0–100). */
  topPct: number;
}

/**
 * Convert metres-from-top-left to percentage offsets over the plot-plan image.
 *
 * @param xM      Asset X position in metres (left edge = 0)
 * @param yM      Asset Y position in metres (top edge = 0)
 * @param widthM  Plot-plan total width in metres
 * @param heightM Plot-plan total height in metres
 */
export function toPlotPct(
  xM: number,
  yM: number,
  widthM: number,
  heightM: number
): PlotPct {
  return {
    leftPct: (xM / widthM) * 100,
    topPct: (yM / heightM) * 100,
  };
}

/** Map a health status string to a hex colour for markers and mesh tints.
 *
 * Three visual channels:
 *   crit  → red     (#C0392B)
 *   warn/watch → amber  (#D97706)
 *   ok    → green   (#15803D)
 *   "" / unknown → neutral navy-grey (#5A6B82) — never green, never alarming
 */
const HEALTH_COLORS: Record<string, string> = {
  crit:  "#C0392B",
  warn:  "#D97706",
  watch: "#D97706",
  ok:    "#15803D",
};

const NEUTRAL = "#5A6B82";

export function healthColor(status: string): string {
  return HEALTH_COLORS[status] ?? NEUTRAL;
}
